import { afterNextRender, AfterViewInit, Component, effect, inject, NgZone, OnInit, ViewContainerRef } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Chart } from 'chart.js';
import 'chartjs-adapter-luxon';
import { Settings } from 'luxon';

import { SidenavContentLifecycleService } from '#core/components/sidenav/components/sidenav-content/services/sidenav-content-lifecycle.service';
import { I18nService } from '#core/i18n/i18n.service';
import { CheckForUpdateService } from '#core/services/check-for-updates.service';
import { ErrorsService } from '#core/services/errors.service';
import { MalouErrorsService } from '#core/services/malou-errors.service';
import { ToastService } from '#core/services/toast.service';
import { UnhandledFileDropGuardService } from '#core/services/unhandled-file-drop-guard.service';
import { WebVitalsService } from '#core/services/web-vitals.service';
// This module need to be loaded
import { environment } from '#environments/environment';
import { ChatbotButtonComponent } from '#shared/components/chatbot-button/chatbot-button.component';
import { FooterPopinComponent } from '#shared/components/footer-popin/footer-popin.component';
import { ToastComponent } from '#shared/components/toast/toast.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.template.html',
    imports: [FooterPopinComponent, ChatbotButtonComponent, RouterOutlet, ToastComponent, MatIconModule],
})
export class AppComponent implements OnInit, AfterViewInit {
    private readonly _viewContainerRef = inject(ViewContainerRef);
    private readonly _translateService = inject(TranslateService);
    private readonly _sidenavLifecycle = inject(SidenavContentLifecycleService);
    private readonly _errorsService = inject(ErrorsService);
    private readonly _malouErrorsService = inject(MalouErrorsService);
    private readonly _toastService = inject(ToastService);
    private readonly _checkForUpdateService = inject(CheckForUpdateService);
    private readonly _i18nService = inject(I18nService);
    private readonly _webVitalsService = inject(WebVitalsService);
    private readonly _ngZone = inject(NgZone);
    private readonly _unhandledFileDropGuardService = inject(UnhandledFileDropGuardService);

    // expose signal to template
    readonly isSidenavContentLoaded = this._sidenavLifecycle.isLoaded;

    /** temporary property to support both V1 and V2 media upload */

    constructor() {
        this._unhandledFileDropGuardService.install();
        // Wait for the browser to finish rendering the page before initializing the Web Vitals service
        // in order not to block the main thread with PerformanceObserver instantiations
        afterNextRender(() => {
            // Run outside of Angular zone to avoid triggering Change Detection for each performance metrics
            this._ngZone.runOutsideAngular(() => {
                this._webVitalsService.initialize();
            });
        });
        effect(() => {
            Chart.defaults.locale = this._i18nService.currentLang();
        });
    }

    ngOnInit(): void {
        this._i18nService.init();
        this._initGoogleMapsScript();
        this._initLuxon();
    }

    ngAfterViewInit(): void {
        this._errorsService.caslForbiddenError$.subscribe({
            next: (err) => {
                if (err) {
                    this._toastService.openErrorToast(this._translateService.instant('forbidden_error.error'));
                }
            },
        });
        this._errorsService.forbiddenError$.subscribe({
            next: (err) => {
                if (err?.error?.malouErrorCode) {
                    this._toastService.openErrorToast(this._malouErrorsService.getReadableErrorForMalouErrorCode(err.error.malouErrorCode));
                } else if (err) {
                    console.warn('Dev indication: unhandled forbidden error', err);
                }
            },
        });
        this._checkForUpdateService.init(this._viewContainerRef);
    }

    private _initLuxon(): void {
        if (this._translateService.currentLang) {
            Settings.defaultLocale = this._translateService.currentLang;
        }
        this._translateService.onLangChange.subscribe((langChangeEvent: LangChangeEvent) => {
            Settings.defaultLocale = langChangeEvent.lang;
        });
    }

    private _initGoogleMapsScript(): void {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${environment.GOOGLE_FRONTEND_KEY}`;

        const head = document.getElementsByTagName('head');
        if (head) {
            head[0]?.appendChild(script);
        }
    }
}
